import { useCallback, useState } from 'react';

import { useToast } from '../../LayoutManager';
import {
  blockActions,
  sectionActions,
  useSelectWidgetInstanceEntities,
  widgetInstanceActions,
  sectionSelectors,
} from '../../slices';
import { toggleNewCasesBlocks } from '../../thunks';
import {
  canAddWidget,
  getWidgetLimit,
  type WidgetId,
  type WidgetManagerState,
  widgetManagerActions,
  widgetRegistry,
  type WidgetType,
} from '../../widgetManager';

import { parseGeoMapId, validateGeoMapPlacement } from './geoMapHelpers';
import { useWidgetCounting } from './useWidgetCounting';
import {
  extractWidgetDefaults,
  formatWidgetName,
  generateWidgetId,
} from './utils';

import type { EWidgetSection } from '#shared';
import type { AppDispatch } from '../../redux-store';
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';

interface UseDragHandlersProps {
  dispatch: AppDispatch;
  sectionsById: ReturnType<typeof sectionSelectors.useSelectSectionEntities>;
  draggingWidget: WidgetManagerState['draggingWidget'];
  onDeleteLastWidget?: () => void;
  showToast: ReturnType<typeof useToast>['showToast'];
}

export const useDragHandlers = ({
  dispatch,
  sectionsById,
  draggingWidget,
  onDeleteLastWidget,
  showToast,
}: UseDragHandlersProps) => {
  const widgetInstances = useSelectWidgetInstanceEntities();
  const { countWidgets } = useWidgetCounting();

  const [activeId, setActiveId] = useState<WidgetId | WidgetType | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDragStart = useCallback(
    ({ active }: DragStartEvent) => {
      const id = active.id as WidgetId | WidgetType;
      setActiveId(id);
      setIsDragging(true);

      // Check if this is a palette widget
      const actualWidgetType =
        id === 'geo-map-center' || id === 'geo-map-focus'
          ? 'geo-map'
          : (id as WidgetType);
      const isPaletteWidget = widgetRegistry.has(actualWidgetType);

      if (isPaletteWidget) {
        dispatch(
          widgetManagerActions.setDraggingWidget({
            type: 'widget-prototype',
            id: id as WidgetType,
          }),
        );
      } else {
        const widgetId = id as WidgetId;
        const widget = widgetInstances[widgetId];
        if (widget) {
          dispatch(
            widgetManagerActions.setDraggingWidget({
              type: 'widget-instance',
              id: widgetId,
              widget,
            }),
          );
        }
      }
    },
    [dispatch, widgetInstances],
  );

  const handleDropOnDeleteZone = useCallback(() => {
    if (!draggingWidget) {
      return true;
    }

    if (draggingWidget.type === 'widget-prototype') {
      dispatch(widgetManagerActions.setDraggingWidget(null));
      return true;
    }

    if (draggingWidget.type === 'widget-instance' && sectionsById) {
      const widgetId = draggingWidget.id;

      for (const [sectionId, section] of Object.entries(sectionsById)) {
        if (section?.blockOrder?.includes(widgetId)) {
          if (section.blockOrder.length <= 1) {
            onDeleteLastWidget?.();
            dispatch(widgetManagerActions.setDraggingWidget(null));
            return true;
          }

          dispatch(blockActions.blockRemove(widgetId));
          dispatch(
            sectionActions.sectionRemoveBlock({
              sectionId: sectionId as EWidgetSection,
              blockId: widgetId,
            }),
          );
          break;
        }
      }
    }

    dispatch(widgetManagerActions.setDraggingWidget(null));
    return true;
  }, [draggingWidget, sectionsById, dispatch, onDeleteLastWidget]);

  const handleReplaceWidget = useCallback(
    (
      draggedId: string,
      targetSectionId: EWidgetSection,
      targetWidgetId: WidgetId,
      targetIndex: number,
    ) => {
      const { widgetType, restrictToSection } = parseGeoMapId(
        draggedId,
        targetSectionId,
      );

      const validation = validateGeoMapPlacement(
        draggedId,
        targetSectionId,
        restrictToSection,
      );

      if (!validation.valid) {
        showToast(validation.errorMessage!, 'error', {
          vertical: 'top',
          horizontal: 'center',
        });
        dispatch(widgetManagerActions.setDraggingWidget(null));
        return true;
      }

      // Remove old widget
      dispatch(blockActions.blockRemove(targetWidgetId));
      dispatch(
        sectionActions.sectionRemoveBlock({
          sectionId: targetSectionId,
          blockId: targetWidgetId,
        }),
      );
      dispatch(
        widgetInstanceActions.removeWidgetInstance(
          widgetInstances[targetWidgetId],
        ),
      );

      // Create new widget
      const newWidgetId = generateWidgetId(widgetType);
      const widget = widgetRegistry.get(widgetType);

      if (widget) {
        if (widgetType === 'new-cases') {
          dispatch(toggleNewCasesBlocks(false));
        }

        dispatch(
          widgetInstanceActions.setWidgetInstance({
            id: newWidgetId,
            title: widget.name,
            sectionId: targetSectionId,
            type: widgetType,
            settings: extractWidgetDefaults(widget.config),
          }),
        );
        dispatch(blockActions.blockUpsert({ id: newWidgetId }));
        dispatch(
          sectionActions.sectionAddBlockAtPosition({
            sectionId: targetSectionId,
            widgetType,
            blockId: newWidgetId,
            position: targetIndex,
          }),
        );
      }

      dispatch(widgetManagerActions.setDraggingWidget(null));
      return true;
    },
    [dispatch, showToast, widgetInstances],
  );

  const handleDropOnAddButton = useCallback(
    (draggedId: string, targetSectionId: EWidgetSection, position: number) => {
      const { widgetType, restrictToSection } = parseGeoMapId(
        draggedId,
        targetSectionId,
      );

      const validation = validateGeoMapPlacement(
        draggedId,
        targetSectionId,
        restrictToSection,
      );

      if (!validation.valid) {
        showToast(validation.errorMessage!, 'error', {
          vertical: 'top',
          horizontal: 'center',
        });
        dispatch(widgetManagerActions.setDraggingWidget(null));
        return true;
      }

      // Count and check limits
      const counts = countWidgets(widgetType);
      const limit = getWidgetLimit(widgetType);
      const currentCount = counts.total;
      const currentCountInSection = counts.bySection[targetSectionId] || 0;

      // Special handling for geo-map
      if (widgetType === 'geo-map') {
        if (targetSectionId === 'center' && currentCountInSection >= 1) {
          showToast(
            `Geo Map limit reached in Center: ${currentCountInSection}/1`,
            'warning',
            {
              vertical: 'top',
              horizontal: 'center',
            },
          );
          dispatch(widgetManagerActions.setDraggingWidget(null));
          return true;
        }
      } else {
        if (currentCount > limit) {
          showToast(
            `${formatWidgetName(widgetType)} already exceeds limit (${currentCount}/${limit}). Remove existing widgets before adding more.`,
            'warning',
            {
              vertical: 'top',
              horizontal: 'center',
            },
          );
          dispatch(widgetManagerActions.setDraggingWidget(null));
          return true;
        }

        if (
          !canAddWidget(
            widgetType,
            currentCount,
            targetSectionId,
            currentCountInSection,
          )
        ) {
          showToast(
            `${formatWidgetName(widgetType)} limit reached: ${currentCount}/${limit === Infinity ? '∞' : limit}`,
            'warning',
            {
              vertical: 'top',
              horizontal: 'center',
            },
          );
          dispatch(widgetManagerActions.setDraggingWidget(null));
          return true;
        }
      }

      // Create new widget
      const newWidgetId = generateWidgetId(widgetType);
      const widget = widgetRegistry.get(widgetType);

      if (widget) {
        if (widgetType === 'new-cases') {
          dispatch(toggleNewCasesBlocks(false));
        }

        dispatch(
          widgetInstanceActions.setWidgetInstance({
            id: newWidgetId,
            title: widget.name,
            sectionId: targetSectionId,
            type: widgetType,
            settings: extractWidgetDefaults(widget.config),
          }),
        );
        dispatch(blockActions.blockUpsert({ id: newWidgetId }));
        dispatch(
          sectionActions.sectionAddBlockAtPosition({
            sectionId: targetSectionId,
            widgetType,
            blockId: newWidgetId,
            position,
          }),
        );
      }

      dispatch(widgetManagerActions.setDraggingWidget(null));
      return true;
    },
    [dispatch, showToast, countWidgets],
  );

  const handleWidgetSwap = useCallback(
    (widgetId: WidgetId, sourceSectionId: EWidgetSection, overId: string) => {
      // Check if dropped on a section (empty area) - no action
      const isOverSection = sectionsById[overId];
      if (isOverSection) {
        dispatch(widgetManagerActions.setDraggingWidget(null));
        return true;
      }

      // Find target widget's section
      let targetSectionId: EWidgetSection | null = null;
      for (const [sectionId, section] of Object.entries(sectionsById)) {
        if (section?.blockOrder?.includes(overId as WidgetId)) {
          targetSectionId = sectionId as EWidgetSection;
          break;
        }
      }

      if (!targetSectionId) {
        dispatch(widgetManagerActions.setDraggingWidget(null));
        return false;
      }

      const targetWidgetId = overId as WidgetId;

      // Don't swap with itself
      if (widgetId === targetWidgetId) {
        dispatch(widgetManagerActions.setDraggingWidget(null));
        return true;
      }

      const sourceSection = sectionsById[sourceSectionId];
      const targetSection = sectionsById[targetSectionId];
      const sourceIndex = sourceSection?.blockOrder.indexOf(widgetId) ?? -1;
      const targetIndex =
        targetSection?.blockOrder.indexOf(targetWidgetId) ?? -1;

      if (
        !sourceSection ||
        !targetSection ||
        sourceIndex === -1 ||
        targetIndex === -1
      ) {
        dispatch(widgetManagerActions.setDraggingWidget(null));
        return false;
      }

      // VALIDATION: Check geo-map placement rules
      const sourceWidget = widgetInstances[widgetId];
      const targetWidget = widgetInstances[targetWidgetId];

      if (
        sourceWidget?.type === 'geo-map' &&
        sourceSectionId === 'center' &&
        (targetSectionId === 'left-primary' ||
          targetSectionId === 'right-primary')
      ) {
        showToast('Cannot move Main Map to side panels.', 'error');
        dispatch(widgetManagerActions.setDraggingWidget(null));
        return true;
      }

      if (
        sourceWidget?.type === 'geo-map' &&
        targetSectionId === 'center' &&
        (sourceSectionId === 'left-primary' ||
          sourceSectionId === 'right-primary')
      ) {
        showToast('Cannot move Focus View to center.', 'error');
        dispatch(widgetManagerActions.setDraggingWidget(null));
        return true;
      }

      if (
        targetWidget?.type === 'geo-map' &&
        targetSectionId === 'center' &&
        (sourceSectionId === 'left-primary' ||
          sourceSectionId === 'right-primary')
      ) {
        showToast(
          `Cannot swap out ${targetWidget.title} from center. The Main Map must remain in the center panel.`,
          'error',
        );
        dispatch(widgetManagerActions.setDraggingWidget(null));
        return true;
      }

      if (
        targetWidget?.type === 'geo-map' &&
        (targetSectionId === 'left-primary' ||
          targetSectionId === 'right-primary') &&
        sourceSectionId === 'center'
      ) {
        showToast(
          `Cannot swap ${targetWidget.title} to center. Use the Main Map widget for the center panel.`,
          'error',
        );
        dispatch(widgetManagerActions.setDraggingWidget(null));
        return true;
      }

      // Perform the swap
      const sourceBlockOrder = [...sourceSection.blockOrder];
      const targetBlockOrder =
        sourceSectionId === targetSectionId
          ? sourceBlockOrder
          : [...targetSection.blockOrder];

      sourceBlockOrder[sourceIndex] = targetWidgetId;
      if (sourceSectionId === targetSectionId) {
        sourceBlockOrder[targetIndex] = widgetId;
      } else {
        targetBlockOrder[targetIndex] = widgetId;
      }

      dispatch(
        sectionActions.sectionSetBlocks({
          sectionId: sourceSectionId,
          blockIds: sourceBlockOrder,
        }),
      );

      if (sourceSectionId !== targetSectionId) {
        dispatch(
          sectionActions.sectionSetBlocks({
            sectionId: targetSectionId,
            blockIds: targetBlockOrder,
          }),
        );
        dispatch(
          widgetInstanceActions.setWidgetInstance({
            id: widgetId,
            sectionId: targetSectionId,
          }),
        );
        dispatch(
          widgetInstanceActions.setWidgetInstance({
            id: targetWidgetId,
            sectionId: sourceSectionId,
          }),
        );
      }

      dispatch(widgetManagerActions.setDraggingWidget(null));
      return true;
    },
    [dispatch, sectionsById, showToast, widgetInstances],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { over } = event;
      setActiveId(null);
      setIsDragging(false);

      if (!over || !draggingWidget) {
        dispatch(widgetManagerActions.setDraggingWidget(null));
        return;
      }

      const overId = over.id.toString();

      // Handle drop on delete zone
      if (overId === 'delete-zone') {
        handleDropOnDeleteZone();
        return;
      }

      // Handle dropping prototype widget on existing widget to replace it
      if (draggingWidget.type === 'widget-prototype' && sectionsById) {
        const draggedId = draggingWidget.id;
        const isDroppedOnWidget = !overId.match(/^(.+)__add__(\d+)$/);

        if (isDroppedOnWidget) {
          let targetSectionId: EWidgetSection | null = null;
          let targetWidgetId: WidgetId | null = null;

          for (const [sectionId, section] of Object.entries(sectionsById)) {
            if (section?.blockOrder?.includes(overId as WidgetId)) {
              targetSectionId = sectionId as EWidgetSection;
              targetWidgetId = overId as WidgetId;
              break;
            }
          }

          if (targetSectionId && targetWidgetId) {
            const section = sectionsById[targetSectionId];
            if (section) {
              const targetIndex = section.blockOrder.indexOf(targetWidgetId);
              if (targetIndex !== -1) {
                handleReplaceWidget(
                  draggedId,
                  targetSectionId,
                  targetWidgetId,
                  targetIndex,
                );
                return;
              }
            }
          }
        }
      }

      // Check if dropped on an add button
      const addButtonMatch = overId.match(/^(.+)__add__(\d+)$/);
      if (addButtonMatch) {
        const targetSectionId = addButtonMatch[1] as EWidgetSection;
        const position = parseInt(addButtonMatch[2], 10);

        // Handle widget prototype (from palette)
        if (draggingWidget.type === 'widget-prototype') {
          const draggedId = draggingWidget.id;
          handleDropOnAddButton(draggedId, targetSectionId, position);
          return;
        }

        // Handle widget instance (from another section)
        if (draggingWidget.type === 'widget-instance' && sectionsById) {
          const widgetId = draggingWidget.id;

          // Find source section
          let sourceSectionId: EWidgetSection | null = null;
          for (const [sectionId, section] of Object.entries(sectionsById)) {
            if (section?.blockOrder?.includes(widgetId)) {
              sourceSectionId = sectionId as EWidgetSection;
              break;
            }
          }

          if (sourceSectionId) {
            // Prevent dropping on add button within the same section
            if (sourceSectionId === targetSectionId) {
              dispatch(widgetManagerActions.setDraggingWidget(null));
              return;
            }

            // VALIDATION: Check geo-map placement rules
            const sourceWidget = widgetInstances[widgetId];

            if (
              sourceWidget?.type === 'geo-map' &&
              sourceSectionId === 'center' &&
              (targetSectionId === 'left-primary' ||
                targetSectionId === 'right-primary')
            ) {
              showToast('Cannot move Main Map to side panels.', 'error');
              dispatch(widgetManagerActions.setDraggingWidget(null));
              return;
            }

            if (
              sourceWidget?.type === 'geo-map' &&
              targetSectionId === 'center' &&
              (sourceSectionId === 'left-primary' ||
                sourceSectionId === 'right-primary')
            ) {
              showToast('Cannot move Focus View to center.', 'error');
              dispatch(widgetManagerActions.setDraggingWidget(null));
              return;
            }

            // Move widget to the target section at the specified position
            dispatch(
              sectionActions.sectionRemoveBlock({
                sectionId: sourceSectionId,
                blockId: widgetId,
              }),
            );
            dispatch(
              sectionActions.sectionAddBlockAtPosition({
                sectionId: targetSectionId,
                widgetType: draggingWidget.widget.type,
                blockId: widgetId,
                position,
              }),
            );
            dispatch(
              widgetInstanceActions.setWidgetInstance({
                id: widgetId,
                sectionId: targetSectionId,
              }),
            );
            dispatch(widgetManagerActions.setDraggingWidget(null));
            return;
          }
        }
      }

      // Handle dropping widget prototype directly on an empty section
      if (draggingWidget.type === 'widget-prototype' && sectionsById) {
        const targetSection = sectionsById[overId as EWidgetSection];
        if (targetSection && targetSection.blockOrder.length === 0) {
          // Section is empty, add widget at position 0
          const draggedId = draggingWidget.id;
          handleDropOnAddButton(draggedId, overId as EWidgetSection, 0);
          return;
        }
      }

      // Handle widget instance reordering/moving
      if (draggingWidget.type === 'widget-instance' && sectionsById) {
        const widgetId = draggingWidget.id;

        let sourceSectionId: EWidgetSection | null = null;
        for (const [sectionId, section] of Object.entries(sectionsById)) {
          if (section?.blockOrder?.includes(widgetId)) {
            sourceSectionId = sectionId as EWidgetSection;
            break;
          }
        }

        if (sourceSectionId) {
          // Check if dropped on an empty section
          const targetSection = sectionsById[overId as EWidgetSection];
          if (targetSection && targetSection.blockOrder.length === 0) {
            // Move widget to empty section at position 0
            dispatch(
              sectionActions.sectionRemoveBlock({
                sectionId: sourceSectionId,
                blockId: widgetId,
              }),
            );
            dispatch(
              sectionActions.sectionAddBlockAtPosition({
                sectionId: overId as EWidgetSection,
                widgetType: draggingWidget.widget.type,
                blockId: widgetId,
                position: 0,
              }),
            );
            dispatch(
              widgetInstanceActions.setWidgetInstance({
                id: widgetId,
                sectionId: overId as EWidgetSection,
              }),
            );
            dispatch(widgetManagerActions.setDraggingWidget(null));
            return;
          }

          // Otherwise, try to swap with another widget
          const handled = handleWidgetSwap(widgetId, sourceSectionId, overId);
          if (handled) {
            return;
          }
        }
      }

      dispatch(widgetManagerActions.setDraggingWidget(null));
    },
    [
      draggingWidget,
      sectionsById,
      dispatch,
      handleDropOnDeleteZone,
      handleReplaceWidget,
      handleDropOnAddButton,
      widgetInstances,
      showToast,
      handleWidgetSwap,
    ],
  );

  const handleDragCancel = useCallback(() => {
    setActiveId(null);
    setIsDragging(false);
    dispatch(widgetManagerActions.setDraggingWidget(null));
  }, [dispatch]);

  return {
    activeId,
    isDragging,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
  };
};
